import { haptic } from "@/lib/haptic";
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import {
  Send,
  Loader2,
  Search as SearchIcon,
  Maximize2,
  ArrowLeft,
  Pin,
  CheckSquare,
  ChevronRight,
  FileText,
  Undo2,
  Redo2,
  Plus,
  Bold,
  Italic,
  Underline,
  List,
  MoreHorizontal,
  Mic,
  Folder as FolderIcon,
  Sparkles,
  X,
  Camera,
  Image as GalleryIcon,
  Link as LinkIcon,
  Calendar,
  Clock,
  Flag,
  Repeat,
} from "lucide-react";
import aiIcon from "@/assets/9861-removebg-preview.png";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useListNotes,
  useCreateNote,
  useUploadImage,
  useListFolders,
  useAssignNoteFolder,
  useCreateTask,
  getListNotesQueryKey,
  getListTasksQueryKey,
} from "@workspace/api-client-react";
import { format } from "date-fns";
import { stripHtml } from "@/lib/strip-html";
import { markdownFromClipboard, markdownIfPlain } from "@/lib/markdown";
import { buildLinkHtml, isEmbeddableVideo, handleContentLinkClick, type LinkDisplayType } from "@/lib/link-utils";

// Converts contentEditable HTML (Chrome wraps each line in <div>...</div>)
// into readable plain text with line breaks preserved, for display in the
// plain Textarea. Only used for display — the underlying tempContent keeps
// the original HTML until the user actually edits it in plain mode.
function htmlToPlainText(html: string): string {
  if (!/<[a-z][\s\S]*>/i.test(html)) return html;
  const withBreaks = html
    .replace(/<div[^>]*>/gi, "\n")
    .replace(/<\/div>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<p>/gi, "");
  const div = document.createElement("div");
  div.innerHTML = withBreaks;
  const text = div.textContent || div.innerText || "";
  return text.replace(/\n{3,}/g, "\n\n").replace(/^\n+/, "");
}
import { cn } from "@/lib/utils";

type CaptureMode = "title" | "note" | "search" | "remember";

export function CapturePage() {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [mode, setMode] = React.useState<CaptureMode>("note");
  const [keyboardOffset, setKeyboardOffset] = React.useState(0);

  React.useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const handleResize = () => {
      const offset = window.innerHeight - vv.height - vv.offsetTop;
      setKeyboardOffset(offset > 0 ? offset : 0);
    };
    vv.addEventListener("resize", handleResize);
    vv.addEventListener("scroll", handleResize);
    handleResize();
    return () => {
      vv.removeEventListener("resize", handleResize);
      vv.removeEventListener("scroll", handleResize);
    };
  }, []);
  const search = useSearch();
  const searchParams = React.useMemo(
    () => new URLSearchParams(search),
    [search],
  );
  // Fullscreen state is derived entirely from the URL instead of local
  // state: "/?expanded=1" for the plain flow, "/?expanded=1&folderId=X" for
  // the folder quick-create flow. useSearch() reacts to real navigation —
  // including the native popstate fired by a swipe-back gesture or the
  // hardware back button — so no custom history bookkeeping or popstate
  // listener is needed. The browser's own back behavior is the source of
  // truth.
  const isExpanded = searchParams.get("expanded") === "1";
  const quickCreateFolderIdParam = searchParams.get("folderId");
  const isQuickCreate = isExpanded && quickCreateFolderIdParam !== null;

  // Seed the composer's folder selection from the URL when landing here via
  // a folder's quick-create link, so the note saves into the right folder.
  React.useEffect(() => {
    if (isQuickCreate && quickCreateFolderIdParam) {
      const parsed = Number(quickCreateFolderIdParam);
      if (!Number.isNaN(parsed)) setSelectedFolderId(parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuickCreate, quickCreateFolderIdParam]);

  // Closing fullscreen is just a real back-navigation — the URL that got us
  // here ("/" or "/browse?folderId=X") is still the previous history entry,
  // so this lands exactly where we came from, the same way a swipe-back
  // gesture would.
  const closeFullscreen = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/");
    }
  };
  const [customBg, setCustomBg] = React.useState<string | null>(null);
  React.useEffect(() => {
    setCustomBg(localStorage.getItem("rememly-capture-background"));
  }, []);
  // Quick-create is now handled by deriving isExpanded/isQuickCreate from
  // the URL above — no mount effect needed here anymore.
  const [deepSearch, setDeepSearch] = React.useState(false);
  const [aiChatMode, setAiChatMode] = React.useState(false);
  // "Remember this" (task) composer state
  const [taskDueDate, setTaskDueDate] = React.useState<string>("");
  const [taskDueTime, setTaskDueTime] = React.useState<string>("");
  const [taskPriority, setTaskPriority] = React.useState<
    "none" | "low" | "medium" | "high"
  >("none");
  const [taskRepeat, setTaskRepeat] = React.useState<
    "none" | "daily" | "weekly" | "weekday" | "monthly" | "yearly" | "custom"
  >("none");
  const [openCustomRepeat, setOpenCustomRepeat] = React.useState(false);
  const [customInterval, setCustomInterval] = React.useState(1);
  const [customUnit, setCustomUnit] = React.useState<
    "day" | "week" | "month" | "year"
  >("day");
  const [customBasedOn, setCustomBasedOn] = React.useState<
    "scheduled" | "completed"
  >("scheduled");
  const [customEndsOn, setCustomEndsOn] = React.useState(false);
  const [customEndsAt, setCustomEndsAt] = React.useState("");
  const handleRepeatPick = (r: typeof taskRepeat) => {
    if (r === "custom") {
      setTimeout(() => setOpenCustomRepeat(true), 80);
    } else {
      setTaskRepeat(r);
    }
  };
  const getCustomSummary = (): string => {
    const every =
      customInterval === 1
        ? "Every " + customUnit
        : "Every " + customInterval + " " + customUnit + "s";
    if (customEndsOn && customEndsAt) {
      return (
        every +
        ", until " +
        format(new Date(customEndsAt + "T00:00:00"), "MMM d")
      );
    }
    return every;
  };
  const customEndsAtIso = (): string | null =>
    customEndsOn && customEndsAt
      ? new Date(customEndsAt + "T23:59:59").toISOString()
      : null;
  const taskDateInputRef = React.useRef<HTMLInputElement>(null);
  const taskTimeInputRef = React.useRef<HTMLInputElement>(null);
  const getRepeatLabel = (
    r: "none" | "daily" | "weekly" | "weekday" | "monthly" | "yearly" | "custom",
    dueDate: string,
  ): string => {
    const date = dueDate ? new Date(dueDate + "T00:00:00") : null;
    switch (r) {
      case "none":
        return "Clear";
      case "daily":
        return "Every day";
      case "weekly": {
        const weekday = date
          ? date.toLocaleDateString("en-US", { weekday: "long" })
          : "";
        return weekday ? "Every week on " + weekday : "Every week";
      }
      case "weekday":
        return "Every weekday (Mon-Fri)";
      case "monthly": {
        const day = date ? date.getDate() : null;
        return day ? "Every month on the " + day : "Every month";
      }
      case "yearly": {
        const label = date
          ? date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : "";
        return label ? "Every year on " + label : "Every year";
      }
      case "custom":
        return "Custom…";
      default:
        return r;
    }
  };
  const combineTaskDueAt = (): string | null => {
    if (!taskDueDate) return null;
    const time = taskDueTime || "09:00";
    const combined = new Date(`${taskDueDate}T${time}`);
    return Number.isNaN(combined.getTime()) ? null : combined.toISOString();
  };
  const resetTaskFields = () => {
    setTaskDueDate("");
    setTaskDueTime("");
    setTaskPriority("none");
    setTaskRepeat("none");
    setCustomInterval(1);
    setCustomUnit("day");
    setCustomBasedOn("scheduled");
    setCustomEndsOn(false);
    setCustomEndsAt("");
  };
  const [openFolderSheet, setOpenFolderSheet] = React.useState(false);
  const [selectedFolderId, setSelectedFolderId] = React.useState<number | null>(
    null,
  );
  const [folderSearchQuery, setFolderSearchQuery] = React.useState("");
  const [tempTitle, setTempTitle] = React.useState("");
  const [tempContent, setTempContent] = React.useState("");
  // True when tempContent holds rich link markup (a bookmark card, embed,
  // etc). Typing directly in the compact plain-text box would flatten that
  // HTML into plain text and destroy the link, so the compact view is made
  // non-editable (with a tap-to-continue prompt) whenever this is true.
  const hasRichLinkContent = (html: string): boolean =>
    /<a\s|<iframe|contenteditable="false"/i.test(html);
  const [pendingImageUrls, setPendingImageUrls] = React.useState<string[]>([]);
  const [linkSheetOpen, setLinkSheetOpen] = React.useState(false);
  const [linkSheetUrl, setLinkSheetUrl] = React.useState("");
  const [imagePosition, setImagePosition] = React.useState<"before" | "after">("before");
  const [searchQuery, setSearchQuery] = React.useState("");
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const expandedTitleRef = React.useRef<HTMLInputElement>(null);
  const expandedContentRef = React.useRef<HTMLDivElement>(null);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);
  const galleryInputRef = React.useRef<HTMLInputElement>(null);

  type DraftSnapshot = { title: string; content: string };
  const HISTORY_GROUP_MS = 400;
  const MAX_HISTORY = 500;
  const draftHistoryRef = React.useRef({ title: "", content: "" });
  const historyTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [draftHistory, setDraftHistory] = React.useState<{
    stack: DraftSnapshot[];
    index: number;
  }>({ stack: [{ title: "", content: "" }], index: 0 });

  const pushDraftHistory = (snap: DraftSnapshot) => {
    setDraftHistory((prev) => {
      const last = prev.stack[prev.index];
      if (last.title === snap.title && last.content === snap.content)
        return prev;
      const trimmed = prev.stack.slice(0, prev.index + 1);
      const nextStack = [...trimmed, snap].slice(-MAX_HISTORY);
      return { stack: nextStack, index: nextStack.length - 1 };
    });
  };

  const scheduleDraftHistoryPush = (snap: DraftSnapshot) => {
    if (historyTimerRef.current) clearTimeout(historyTimerRef.current);
    historyTimerRef.current = setTimeout(
      () => pushDraftHistory(snap),
      HISTORY_GROUP_MS,
    );
  };

  const applyDraftSnapshot = (snap: DraftSnapshot) => {
    setTempTitle(snap.title);
    setTempContent(snap.content);
    draftHistoryRef.current = { ...snap };
  };

  const handleDraftUndo = () => {
    setDraftHistory((prev) => {
      if (prev.index === 0) return prev;
      const nextIndex = prev.index - 1;
      applyDraftSnapshot(prev.stack[nextIndex]);
      return { ...prev, index: nextIndex };
    });
  };

  const handleDraftRedo = () => {
    setDraftHistory((prev) => {
      if (prev.index >= prev.stack.length - 1) return prev;
      const nextIndex = prev.index + 1;
      applyDraftSnapshot(prev.stack[nextIndex]);
      return { ...prev, index: nextIndex };
    });
  };

  const canDraftUndo = draftHistory.index > 0;
  const canDraftRedo = draftHistory.index < draftHistory.stack.length - 1;
  const { data: notes = [], isLoading: isLoadingNotes } = useListNotes();
  const createNote = useCreateNote();
  const uploadImage = useUploadImage();
  const createTask = useCreateTask();
  const { data: folders = [] } = useListFolders();
  const assignFolder = useAssignNoteFolder();
  const filteredFolders = folders.filter((folder) =>
    folder.name.toLowerCase().includes(folderSearchQuery.toLowerCase()),
  );
  const selectedFolder = folders.find((f) => f.id === selectedFolderId);
  const pinnedCount = notes.filter((n) => n.pinned).length;
  const recentNotes = [...notes]
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .slice(0, 5);
  const formatNoteDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    if (isToday) return format(date, "h:mm a");
    if (isYesterday) return "Yesterday";
    return format(date, "MMM d");
  };

  // Scroll to bottom when notes change or when creating
  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [notes.length, createNote.isPending]);
  React.useEffect(() => {
    if (isExpanded) {
      if (expandedContentRef.current) {
        expandedContentRef.current.innerHTML =
          stripInlineTextColor(tempContent);
      }
      if (mode === "title") {
        expandedTitleRef.current?.focus();
      } else {
        expandedContentRef.current?.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  // Keep the contentEditable in sync when content changes externally
  // (undo/redo), without fighting the user's own typing/cursor position.
  const lastSyncedContentRef = React.useRef(tempContent);
  React.useEffect(() => {
    if (!isExpanded) return;
    if (tempContent === lastSyncedContentRef.current) return;
    lastSyncedContentRef.current = tempContent;
    if (
      expandedContentRef.current &&
      expandedContentRef.current.innerHTML !== tempContent
    ) {
      expandedContentRef.current.innerHTML = stripInlineTextColor(tempContent);
    }
  }, [tempContent, isExpanded]);
  const reset = () => {
    setPendingImageUrls([]);
    setImagePosition("before");
    setTempTitle("");
    setTempContent("");
    setMode("note");
    draftHistoryRef.current = { title: "", content: "" };
    setDraftHistory({ stack: [{ title: "", content: "" }], index: 0 });
    resetTaskFields();
  };

  const switchMode = (next: CaptureMode) => {
    setMode(next);
    if (next === "search") {
      // Entering search mode always starts from a clean input.
      setSearchQuery("");
    }
    inputRef.current?.focus();
  };

  // In "title" mode the primary action just advances to "note" mode.
  // In "note" mode it saves the note (title + content together) and resets.
  // In "search" mode it runs a semantic search and shows results in the Browser page — nothing is saved.
  const handlePrimaryAction = () => {
    if (mode === "title") {
      switchMode("note");
      return;
    }

    if (mode === "search") {
      const q = searchQuery.trim();
      if (!q) return;
      setLocation(`/search?q=${encodeURIComponent(q)}`);
      return;
    }

    if (mode === "remember") {
      const title = tempContent.trim();
      if (!title) return;
      createTask.mutate(
        {
          data: {
            title,
            dueAt: combineTaskDueAt(),
            priority: taskPriority,
            repeat: taskRepeat,
              repeatInterval: taskRepeat === "custom" ? customInterval : null,
              repeatUnit: taskRepeat === "custom" ? customUnit : null,
              repeatBasedOn: taskRepeat === "custom" ? customBasedOn : null,
              repeatEndsAt: taskRepeat === "custom" ? customEndsAtIso() : null,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListTasksQueryKey() });
            reset();
          },
        },
      );
      return;
    }

    if (!tempContent.trim() && pendingImageUrls.length === 0) return;

    createNote.mutate(
      { data: { title: tempTitle.trim(), content: markdownIfPlain(tempContent), imageUrls: pendingImageUrls.length ? pendingImageUrls : undefined, imagePosition } },
      {
        onSuccess: (newNote) => {
          if (selectedFolderId != null) {
            assignFolder.mutate(
              { id: newNote.id, data: { folderId: selectedFolderId } },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: getListNotesQueryKey(),
                  });
                },
              },
            );
            setSelectedFolderId(null);
          } else {
            queryClient.invalidateQueries({ queryKey: getListNotesQueryKey() });
          }
          reset();
        },
      },
    );
  };
  const handleExpandedSave = () => {
    if (!tempContent.trim() && pendingImageUrls.length === 0) return;
    createNote.mutate(
      { data: { title: tempTitle.trim(), content: markdownIfPlain(tempContent), imageUrls: pendingImageUrls.length ? pendingImageUrls : undefined, imagePosition } },
      {
        onSuccess: (newNote) => {
          if (selectedFolderId != null) {
            assignFolder.mutate(
              { id: newNote.id, data: { folderId: selectedFolderId } },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: getListNotesQueryKey(),
                  });
                },
              },
            );
            setSelectedFolderId(null);
          } else {
            queryClient.invalidateQueries({ queryKey: getListNotesQueryKey() });
          }
          reset();
          closeFullscreen();
        },
      },
    );
  };
  const inputValue =
    mode === "title"
      ? tempTitle
      : mode === "search"
        ? searchQuery
        : htmlToPlainText(tempContent);
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim().length >= 2) {
      setLocation(`/search?q=${encodeURIComponent(value.trim())}`);
    }
  };

  const handleTitleOrContentChange = (
    field: "title" | "content",
    value: string,
  ) => {
    if (field === "title") setTempTitle(value);
    else setTempContent(value);
    const nextSnap = {
      title: field === "title" ? value : tempTitle,
      content: field === "content" ? value : tempContent,
    };
    draftHistoryRef.current = nextSnap;
    scheduleDraftHistoryPush(nextSnap);
  };

  const handleInputChange =
    mode === "title"
      ? (value: string) => handleTitleOrContentChange("title", value)
      : mode === "search"
        ? handleSearchChange
        : (value: string) => handleTitleOrContentChange("content", value);

  const syncExpandedContentFromDom = () => {
    const html = expandedContentRef.current?.innerHTML ?? "";
    handleTitleOrContentChange("content", html);
  };

  const applyExpandedFormat = (command: string) => {
    expandedContentRef.current?.focus();
    document.execCommand(command, false);
    syncExpandedContentFromDom();
  };

  const toolbarMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };
  const handleExpandedPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const html = markdownFromClipboard(e.clipboardData);
    if (html) {
      document.execCommand("insertHTML", false, html);
    } else {
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    }
    syncExpandedContentFromDom();
  };
  // Strips only "color:" from inline styles (leaves border-color,
  // background-color, etc. untouched) so old notes with baked-in text
  // colors from other apps/themes always use the current theme color.
  const stripInlineTextColor = (html: string): string => {
    return html.replace(/style="([^"]*)"/g, (_match, styleContent) => {
      const filtered = styleContent
        .split(";")
        .map((decl: string) => decl.trim())
        .filter((decl: string) => {
          if (!decl) return false;
          const prop = decl.split(":")[0]?.trim().toLowerCase();
          return prop !== "color";
        })
        .join("; ");
      return filtered ? `style="${filtered}"` : "";
    });
  };

  const insertLink = (type: LinkDisplayType) => {
    const url = linkSheetUrl.trim();
    if (!url) return;
    const html = buildLinkHtml(url, type);
    handleTitleOrContentChange("content", tempContent ? tempContent + html : html);
    setLinkSheetOpen(false);
    setLinkSheetUrl("");
    if (!isExpanded) setLocation("/?expanded=1");
  };

  const vibrate = haptic;
  return (
    <>
      <div
        className={`max-w-4xl mx-auto min-h-[calc(100dvh-4rem)] flex flex-col px-4 relative ${
          customBg ? "bg-transparent" : "bg-background"
        }`}
      >
        {/* Welcome */}
        <div className="pt-4 pb-1 shrink-0">
          <div className="flex items-start gap-2.5">
            <div className="h-9 w-9 shrink-0 rounded-full bg-black flex items-center justify-center overflow-hidden">
              <img src={aiIcon} alt="" className="h-6 w-6 object-contain" />
            </div>
            <div className="rounded-2xl rounded-tl-sm bg-card/60 backdrop-blur-xl border border-border/50 px-4 py-3 shadow-[0_10px_30px_rgba(0,0,0,0.08)] max-w-[85%]">
              <p className="text-sm font-semibold bg-gradient-to-r from-blue-500 to-cyan-400 bg-clip-text text-transparent">
                Welcome, Vinay
              </p>
              <p className="text-sm text-foreground font-medium mt-0.5">
                Your second brain, simplified
              </p>
            </div>
          </div>
        </div>
        <div className="pl-[46px] pt-0.5 space-y-1.5">
          <div className="rounded-2xl bg-card/60 backdrop-blur-xl border border-border/50 px-4 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)] flex w-fit items-center gap-1.5 text-sm transition-all duration-150 active:scale-[0.97] active:shadow-[0_0_20px_rgba(59,130,246,0.3)]">
            <button
              type="button"
              onClick={() => {
                vibrate();
                setLocation("/browse?openFolders=1");
              }}
              className="text-foreground font-medium"
            >
              Folders
            </button>
            <span className="text-muted-foreground">·</span>
            <button
              type="button"
              onClick={() => {
                vibrate();
                setLocation("/browse");
              }}
              className="text-foreground font-medium"
            >
              All Notes
            </button>
          </div>
          <div className="rounded-2xl bg-card/60 backdrop-blur-xl border border-border/50 px-4 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.08)] flex w-fit items-center gap-1.5 text-sm transition-all duration-150 active:scale-[0.97] active:shadow-[0_0_20px_rgba(59,130,246,0.3)]">
            <button
              type="button"
              onClick={() => {
                vibrate();
                setLocation("/pinned");
              }}
              className="text-foreground font-medium"
            >
              Pinned
            </button>
            <span className="text-muted-foreground">·</span>
            <button
              type="button"
              onClick={() => {
                vibrate();
                setLocation("/tasks");
              }}
              className="text-foreground font-medium"
            >
              Tasks
            </button>
          </div>
        </div>

        <div className="flex-1" />

        {/* Input Area */}
        <div
          className={`pt-0 pb-2 px-0 -mx-0 shrink-0 rounded-t-3xl z-10 sticky bottom-0 mt-auto space-y-2 ${
            customBg ? "bg-transparent" : "bg-card/40 backdrop-blur-xl"
          }`}
        >
          {!aiChatMode && (
            <div className="flex items-center gap-1.5 sm:gap-2 pl-1 pr-4">
              {/* Title */}
              <Button
                type="button"
                size="sm"
                variant={mode === "title" ? "default" : "outline"}
                onClick={() => {
                  vibrate();
                  switchMode("title");
                }}
                disabled={createNote.isPending}
                className={`h-8 sm:h-9 rounded-full px-2.5 sm:px-4 text-xs sm:text-sm font-medium border transition-all active:scale-95 shrink-0 ${
                  mode === "title"
                    ? "bg-gradient-to-br from-blue-500 via-cyan-400 to-blue-400 text-white shadow-[0_6px_20px_rgba(34,211,238,0.35)]"
                    : "bg-card/50 border-border/50"
                }`}
              >
                Title
              </Button>

              {/* Note */}
              <Button
                type="button"
                size="sm"
                variant={mode === "note" ? "default" : "outline"}
                onClick={() => {
                  vibrate();
                  switchMode("note");
                }}
                disabled={createNote.isPending}
                className={`h-8 sm:h-9 rounded-full px-2.5 sm:px-4 text-xs sm:text-sm font-medium border transition-all active:scale-95 shrink-0 ${
                  mode === "note"
                    ? "bg-gradient-to-br from-blue-500 via-cyan-400 to-blue-400 text-white shadow-[0_6px_20px_rgba(34,211,238,0.35)]"
                    : "bg-card/50 border-border/50"
                }`}
              >
                Note
              </Button>

              {/* Search */}
              <Button
                type="button"
                size="sm"
                variant={mode === "search" ? "default" : "outline"}
                onClick={() => {
                  vibrate();
                  switchMode("search");
                }}
                disabled={createNote.isPending}
                className={`h-8 sm:h-9 rounded-full px-2.5 sm:px-4 text-xs sm:text-sm font-medium border transition-all active:scale-95 shrink-0 ${
                  mode === "search"
                    ? "bg-gradient-to-br from-blue-500 via-cyan-400 to-blue-400 text-white shadow-[0_6px_20px_rgba(34,211,238,0.35)]"
                    : "bg-card/50 border-border/50"
                }`}
              >
                Search
              </Button>
              {/* Remember this */}
              <Button
                type="button"
                size="sm"
                variant={mode === "remember" ? "default" : "outline"}
                onClick={() => {
                  vibrate();
                  switchMode("remember");
                }}
                disabled={createNote.isPending}
                className={`h-8 sm:h-9 rounded-full px-2.5 sm:px-4 text-xs sm:text-sm font-medium border transition-all active:scale-95 shrink-0 ${
                  mode === "remember"
                    ? "bg-gradient-to-br from-blue-500 via-cyan-400 to-blue-400 text-white shadow-[0_6px_20px_rgba(34,211,238,0.35)]"
                    : "bg-card/50 border-border/50"
                }`}
              >
                Remember this
              </Button>
            </div>
          )}
          {aiChatMode && (
            <button
              type="button"
              onClick={() => setAiChatMode(false)}
              className="block w-full text-right px-1 pb-1 text-sm font-medium text-foreground/70 hover:text-foreground transition-colors"
            >
              Back
            </button>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handlePrimaryAction();
            }}
          >
            <div className="w-full rounded-3xl border border-border/50 bg-card/60 backdrop-blur-xl shadow-[0_10px_35px_rgba(0,0,0,0.10)] focus-within:shadow-[0_0_0_1.5px_rgba(34,211,238,0.5),0_0_25px_rgba(34,211,238,0.25)] flex flex-col overflow-hidden relative transition-all">
                {pendingImageUrls.length > 0 && (<div className="flex items-center gap-2 px-4 pt-3 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{pendingImageUrls.map((url, idx) => (<div key={url + idx} className="relative shrink-0"><img src={url} alt="Attached" className="h-16 w-16 rounded-lg object-cover" /><button type="button" onClick={() => setPendingImageUrls((prev) => prev.filter((_, i) => i !== idx))} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-background border border-border flex items-center justify-center text-xs leading-none text-muted-foreground hover:text-foreground" aria-label="Remove image"><span className="-translate-y-0.5">×</span></button></div>))}</div>)}
              {mode === "note" && !aiChatMode && hasRichLinkContent(tempContent) ? (
                <button
                  type="button"
                  onClick={() => setLocation("/?expanded=1")}
                  className="w-full text-left px-5 py-4 text-base min-h-[52px] text-muted-foreground/70"
                >
                  Link added — tap to keep editing
                </button>
              ) : (
              <Textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onInput={(e) => {
                  const el = e.currentTarget;
                  el.style.height = "auto";

                  const maxHeight = 200;

                  if (el.scrollHeight > maxHeight) {
                    el.style.height = maxHeight + "px";
                    el.style.overflowY = "auto";
                  } else {
                    el.style.height = el.scrollHeight + "px";
                    el.style.overflowY = "hidden";
                  }
                }}
                placeholder={
                  aiChatMode
                    ? "What can Remember AI help you with?"
                    : mode === "title"
                      ? "Title (optional)"
                      : mode === "search"
                        ? deepSearch
                          ? "Search deeper with AI"
                          : "Find anything in your notes"
                        : mode === "note"
                          ? "What's worth remembering?"
                          : "Your thoughts, remembered for later."
                }
                className="w-full !border-0 !shadow-none focus-visible:ring-0 px-5 py-4 text-base min-h-[52px] resize-none"
                disabled={createNote.isPending}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.ctrlKey || e.shiftKey)) {
                    e.preventDefault();
                    handlePrimaryAction();
                  }
                }}
                style={{ maxHeight: "200px" }}
              />
              )}
              <div className="flex items-center gap-2 px-3 py-1.5 min-h-[44px]">
                {/* Mic, AI, and Folder are visual placeholders for now —
                    wired up in a future update. */}
                {(mode === "note" || mode === "remember") && !aiChatMode ? (
                  <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        onMouseDown={toolbarMouseDown}
                        className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                        aria-label="Add attachment"
                      >
                        <Plus className="h-5 w-5 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      collisionPadding={8}
                      onCloseAutoFocus={(e) => e.preventDefault()}
                      className="rounded-2xl p-1.5 w-44 shadow-lg border-none space-y-1 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"
                    >
                      {/* Placeholder actions — not wired up yet */}
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); if (mode === "remember") { const url = window.prompt("Paste a link"); if (url && url.trim()) setTempContent((c) => (c ? c + "\n" + url.trim() : url.trim())); } else { setLinkSheetUrl(""); setLinkSheetOpen(true); } }}
                        className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                      >
                        <LinkIcon className="h-3.5 w-3.5" />
                        Link
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }}
                        className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                      >
                        <Camera className="h-3.5 w-3.5" />
                        Camera
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); galleryInputRef.current?.click(); }}
                        className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                      >
                        <GalleryIcon className="h-3.5 w-3.5" />
                        Gallery
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => e.stopPropagation()}
                        className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                      >
                        <Mic className="h-3.5 w-3.5" />
                        Mic
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) { setImagePosition(tempContent.trim() ? "after" : "before"); uploadImage.mutate({ data: { file } }, { onSuccess: (r) => setPendingImageUrls((prev) => [...prev, r.url]) }); } e.target.value = ""; }} />
                  <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={async (e) => { const files = Array.from(e.target.files ?? []); if (files.length) { setImagePosition(tempContent.trim() ? "after" : "before"); const results = await Promise.all(files.map((file) => uploadImage.mutateAsync({ data: { file } }))); setPendingImageUrls((prev) => [...prev, ...results.map((r) => r.url)]); } e.target.value = ""; }} />
                  </>
                ) : (
                  <button
                    type="button"
                    onMouseDown={toolbarMouseDown}
                    disabled={(mode === "search" && !deepSearch) || aiChatMode}
                    className={cn(
                      "p-2 rounded-full hover:bg-muted transition-colors shrink-0",
                      (mode === "search" && !deepSearch) || aiChatMode
                        ? "opacity-40 pointer-events-none"
                        : "opacity-100",
                    )}
                    aria-label="Voice input"
                    style={aiChatMode ? { display: "none" } : undefined}
                  >
                    <Mic className="h-5 w-5 text-muted-foreground" />
                  </button>
                )}
                {aiChatMode ? (
                  <>
                    <button
                      type="button"
                      onMouseDown={toolbarMouseDown}
                      className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                      aria-label="Add"
                    >
                      <Plus className="h-5 w-5 text-muted-foreground" />
                    </button>
                    <span className="flex items-center gap-1 h-8 px-3 rounded-full text-sm font-medium bg-primary/10 text-primary shrink-0">
                      AI Memory
                    </span>
                  </>
                ) : mode === "search" ? (
                  <button
                    type="button"
                    onClick={() => setDeepSearch((d) => !d)}
                    className={`flex items-center gap-1 h-8 px-3 rounded-full text-sm font-medium border transition-all active:scale-95 shrink-0 ${
                      deepSearch
                        ? "bg-gradient-to-br from-blue-500 to-cyan-400 text-white border-transparent shadow-[0_0_14px_rgba(34,211,238,0.5)]"
                        : "bg-muted/60 text-muted-foreground border-border"
                    }`}
                  >
                    {deepSearch && <Sparkles className="h-3.5 w-3.5" />}
                    Deep
                  </button>
                ) : mode === "remember" ? (
                  <>
                    <input
                      ref={taskDateInputRef}
                      type="date"
                      value={taskDueDate}
                      onChange={(e) => setTaskDueDate(e.target.value)}
                      className="sr-only"
                      tabIndex={-1}
                    />
                    <button
                      type="button"
                      onMouseDown={toolbarMouseDown}
                      onClick={() => {
                        const el = taskDateInputRef.current as
                          | (HTMLInputElement & { showPicker?: () => void })
                          | null;
                        if (!el) return;
                        try {
                          el.showPicker ? el.showPicker() : el.click();
                        } catch {
                          el.click();
                        }
                      }}
                      className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                      aria-label="Date"
                    >
                      <Calendar
                        className={cn(
                          "h-5 w-5",
                          taskDueDate
                            ? "text-primary"
                            : "text-muted-foreground",
                        )}
                      />
                    </button>
                    <input
                      ref={taskTimeInputRef}
                      type="time"
                      value={taskDueTime}
                      onChange={(e) => setTaskDueTime(e.target.value)}
                      className="sr-only"
                      tabIndex={-1}
                    />
                    <button
                      type="button"
                      onMouseDown={toolbarMouseDown}
                      onClick={() => {
                        const el = taskTimeInputRef.current as
                          | (HTMLInputElement & { showPicker?: () => void })
                          | null;
                        if (!el) return;
                        try {
                          el.showPicker ? el.showPicker() : el.click();
                        } catch {
                          el.click();
                        }
                      }}
                      className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                      aria-label="Time"
                    >
                      <Clock
                        className={cn(
                          "h-5 w-5",
                          taskDueTime
                            ? "text-primary"
                            : "text-muted-foreground",
                        )}
                      />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          onMouseDown={toolbarMouseDown}
                          className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                          aria-label="Prioritize"
                        >
                          <Flag
                            className={cn(
                              "h-5 w-5",
                              taskPriority === "high"
                                ? "text-red-500"
                                : taskPriority === "medium"
                                  ? "text-orange-500"
                                  : taskPriority === "low"
                                    ? "text-blue-500"
                                    : "text-muted-foreground",
                            )}
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        collisionPadding={8}
                        className="rounded-2xl p-1.5 w-36 shadow-lg border-none space-y-1"
                      >
                        {(["none", "low", "medium", "high"] as const).map(
                          (p) => (
                            <DropdownMenuItem
                              key={p}
                              onClick={() => setTaskPriority(p)}
                              className={cn(
                                "rounded-lg px-2.5 py-2 text-sm font-medium capitalize bg-muted/60 focus:bg-muted",
                                taskPriority === p &&
                                  "bg-primary/10 text-primary",
                              )}
                            >
                              {p}
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          onMouseDown={toolbarMouseDown}
                          className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                          aria-label="Repeat"
                        >
                          <Repeat
                            className={cn(
                              "h-5 w-5",
                              taskRepeat !== "none"
                                ? "text-primary"
                                : "text-muted-foreground",
                            )}
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        collisionPadding={8}
                        className="rounded-2xl p-1.5 w-56 shadow-lg border-none space-y-1"
                      >
                        {(
                          [
                            "daily",
                            "weekly",
                            "weekday",
                            "monthly",
                            "yearly",
                              "custom",
                              "none",
                            ] as const
                        ).map((r) => (
                          <DropdownMenuItem
                            key={r}
                            onClick={() => handleRepeatPick(r)}
                            className={cn(
                              "rounded-lg px-2.5 py-2 text-sm font-medium bg-muted/60 focus:bg-muted",
                              taskRepeat === r && "bg-primary/10 text-primary",
                              r === "none" && "text-destructive",
                            )}
                          >
                            {r === "custom" && taskRepeat === "custom"
                                ? getCustomSummary()
                                : getRepeatLabel(r, taskDueDate)}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                      <Sheet open={openCustomRepeat} onOpenChange={setOpenCustomRepeat}>
                        <SheetContent
                          side="bottom"
                          className="rounded-t-3xl px-5 pb-6 pt-5 max-h-[90vh] overflow-y-auto"
                        >
                          <div className="space-y-5">
                            <h2 className="text-lg font-semibold">Custom repeat</h2>

                            <div className="space-y-2">
                              <div className="text-sm font-medium">Based on</div>
                              <div className="flex gap-2">
                                {(
                                  [
                                    ["scheduled", "Scheduled date"],
                                    ["completed", "Completed date"],
                                  ] as const
                                ).map(([v, label]) => (
                                  <button
                                    key={v}
                                    type="button"
                                    onClick={() => setCustomBasedOn(v)}
                                    className={cn(
                                      "rounded-full px-4 py-2 text-sm font-medium",
                                      customBasedOn === v
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-muted text-foreground",
                                    )}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="space-y-3">
                              <div className="text-sm font-medium">Every</div>
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => setCustomInterval((n) => Math.max(1, n - 1))}
                                  className="h-10 w-10 rounded-full bg-muted text-xl leading-none"
                                  aria-label="Decrease"
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  min={1}
                                  value={customInterval}
                                  onChange={(e) => {
                                    const n = parseInt(e.target.value, 10);
                                    setCustomInterval(
                                      Number.isNaN(n) ? 1 : Math.min(999, Math.max(1, n)),
                                    );
                                  }}
                                  className="h-10 w-16 rounded-lg bg-muted text-center text-base"
                                />
                                <button
                                  type="button"
                                  onClick={() => setCustomInterval((n) => Math.min(999, n + 1))}
                                  className="h-10 w-10 rounded-full bg-muted text-xl leading-none"
                                  aria-label="Increase"
                                >
                                  +
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {(["day", "week", "month", "year"] as const).map((u) => (
                                  <button
                                    key={u}
                                    type="button"
                                    onClick={() => setCustomUnit(u)}
                                    className={cn(
                                      "rounded-full px-4 py-2 text-sm font-medium capitalize",
                                      customUnit === u
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-muted text-foreground",
                                    )}
                                  >
                                    {customInterval === 1 ? u : u + "s"}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <div className="text-sm font-medium">Ends</div>
                              <button
                                type="button"
                                onClick={() => setCustomEndsOn(false)}
                                className={cn(
                                  "w-full rounded-xl px-4 py-3 text-left text-sm font-medium bg-muted/60",
                                  !customEndsOn && "bg-primary/10 text-primary",
                                )}
                              >
                                Never
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setCustomEndsOn(true);
                                  if (!customEndsAt) {
                                    setCustomEndsAt(taskDueDate || format(new Date(), "yyyy-MM-dd"));
                                  }
                                }}
                                className={cn(
                                  "w-full rounded-xl px-4 py-3 text-left text-sm font-medium bg-muted/60",
                                  customEndsOn && "bg-primary/10 text-primary",
                                )}
                              >
                                On date (inclusive)
                              </button>
                              {customEndsOn && (
                                <input
                                  type="date"
                                  value={customEndsAt}
                                  min={taskDueDate || undefined}
                                  onChange={(e) => setCustomEndsAt(e.target.value)}
                                  className="w-full rounded-xl bg-muted px-4 py-3 text-base"
                                />
                              )}
                            </div>

                            <div className="flex justify-end gap-2 pt-1">
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() => setOpenCustomRepeat(false)}
                              >
                                Cancel
                              </Button>
                              <Button
                                type="button"
                                disabled={customEndsOn && !customEndsAt}
                                onClick={() => {
                                  setTaskRepeat("custom");
                                  setOpenCustomRepeat(false);
                                }}
                              >
                                Save
                              </Button>
                            </div>
                          </div>
                        </SheetContent>
                      </Sheet>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onMouseDown={toolbarMouseDown}
                      className="h-11 w-11 flex items-center justify-center rounded-full hover:bg-muted transition-colors shrink-0"
                      aria-label="AI assistant"
                    >
                      <img
                        src={aiIcon}
                        alt=""
                        className="h-9 w-9 object-contain"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenFolderSheet(true)}
                      className={cn(
                        "p-2 rounded-full transition-colors shrink-0",
                        selectedFolderId != null
                          ? "bg-primary/10 hover:bg-primary/15"
                          : "hover:bg-muted",
                      )}
                      aria-label="Choose folder"
                    >
                      <FolderIcon
                        className={cn(
                          "h-5 w-5",
                          selectedFolderId != null
                            ? "text-primary"
                            : "text-muted-foreground",
                        )}
                      />
                    </button>
                    {selectedFolder && (
                      <span className="text-xs font-medium text-primary bg-primary/10 rounded-full px-2 py-1 truncate max-w-[64px] shrink min-w-0">
                        {selectedFolder.name}
                      </span>
                    )}
                  </>
                )}

                <div className="flex-1" />

                {mode !== "search" && mode !== "remember" && !aiChatMode && (
                  <button
                    type="button"
                    onClick={() => setLocation("/?expanded=1")}
                    className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                  >
                    <Maximize2 className="h-4 w-4 mr-1.5 text-muted-foreground" />
                  </button>
                )}
                <Button
                  type="submit"
                  size="icon"
                  onClick={() => haptic()}
                  disabled={
                    (mode === "note" && !tempContent.trim() && pendingImageUrls.length === 0) ||
                    (mode === "remember" && !tempContent.trim()) ||
                    (mode === "search" && !searchQuery.trim()) ||
                    createNote.isPending ||
                    createTask.isPending
                  }
                  className="h-11 w-11 shrink-0 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 text-white shadow-[0_4px_14px_rgba(34,211,238,0.4)] hover:shadow-[0_10px_30px_rgba(34,211,238,0.6)] transition-all active:scale-90 hover:scale-110"
                >
                  {mode === "search" ? (
                    <SearchIcon className="h-4 w-4" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
      {isExpanded && (
        <div className="fixed inset-0 z-50 bg-background flex flex-col pt-4">
          <div className="flex items-center justify-between px-4 mb-4">
            <button
              type="button"
              onClick={closeFullscreen}
              className="p-2 rounded-full hover:bg-muted transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="h-5 w-5 text-muted-foreground" />
            </button>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-4 mr-1">
                <button
                  type="button"
                  onClick={handleDraftUndo}
                  disabled={!canDraftUndo}
                  className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label="Undo"
                >
                  <Undo2 className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={handleDraftRedo}
                  disabled={!canDraftRedo}
                  className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                  aria-label="Redo"
                >
                  <Redo2 className="h-5 w-5" />
                </button>
              </div>

              <button
                type="button"
                onClick={() => { haptic(); handleExpandedSave(); }}
                disabled={!tempContent.trim() || createNote.isPending}
                className="flex items-center gap-1.5 h-9 px-4 rounded-full gradient-primary text-white text-sm font-medium disabled:opacity-40 transition-opacity"
              >
                <Send className="h-4 w-4" />
                Send
              </button>
            </div>
          </div>
          <div
            className="flex-1 overflow-y-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            onClick={(e) => {
              if (e.target !== e.currentTarget) return;
              const el = expandedContentRef.current;
              if (!el) return;
              el.focus();
              const range = document.createRange();
              range.selectNodeContents(el);
              range.collapse(false);
              const sel = window.getSelection();
              sel?.removeAllRanges();
              sel?.addRange(range);
            }}
          >
            <input
              ref={expandedTitleRef}
              value={tempTitle}
              onChange={(e) =>
                handleTitleOrContentChange("title", e.target.value)
              }
              placeholder="Title (optional)"
              className="w-full text-2xl font-semibold bg-transparent border-none outline-none mb-4 placeholder:text-muted-foreground/50"
            />
            <div
              ref={expandedContentRef}
              contentEditable
              suppressContentEditableWarning
              onInput={syncExpandedContentFromDom}
              onPaste={handleExpandedPaste}
              onClick={handleContentLinkClick}
              data-placeholder="Begin writing your note..."
              className="note-content-editor w-full bg-transparent outline-none text-base leading-relaxed min-h-[50px] empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground/50 empty:before:pointer-events-none [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-0.5"
            />
          </div>
          {/* Bottom formatting toolbar. Insert (+), AI, and More are visual
              placeholders for now — wired up in a future update. */}
          <div className="shrink-0 px-4 pb-4 pt-2">
            <div className="flex items-center justify-between gap-1 rounded-full border bg-secondary/90 backdrop-blur-xl px-3 py-2 shadow-sm">
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="Insert"
              >
                <Plus className="h-6 w-6" />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                onClick={() => applyExpandedFormat("bold")}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="Bold"
              >
                <Bold className="h-5 w-5" />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                onClick={() => applyExpandedFormat("italic")}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="Italic"
              >
                <Italic className="h-5 w-5" />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                onClick={() => applyExpandedFormat("underline")}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="Underline"
              >
                <Underline className="h-5 w-5" />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                onClick={() => applyExpandedFormat("insertUnorderedList")}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="Bullet list"
              >
                <List className="h-5 w-5" />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                className="p-2 rounded-full hover:bg-muted transition-colors shrink-0"
                aria-label="AI assistant"
              >
                <img
                  src={aiIcon}
                  alt=""
                  className="h-6 w-6 object-contain scale-225 translate-y-[0.5px]"
                />
              </button>
              <button
                type="button"
                onMouseDown={toolbarMouseDown}
                className="p-2 rounded-full bg-muted text-foreground hover:bg-muted/70 transition-colors shrink-0"
                aria-label="More"
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Folder picker for the note currently being composed */}
      <Sheet open={openFolderSheet} onOpenChange={setOpenFolderSheet}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl flex flex-col max-h-[80vh] overflow-hidden [&>button.absolute]:hidden"
        >
          <div className="relative flex flex-col min-h-0">
            <div className="relative flex items-center justify-center py-1">
              <h2 className="text-sm font-semibold">Save to folder</h2>
              <button
                onClick={() => setOpenFolderSheet(false)}
                className="absolute right-0 h-8 w-8 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <input
              type="text"
              placeholder="Search folders..."
              value={folderSearchQuery}
              onChange={(e) => setFolderSearchQuery(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-muted border border-transparent focus:outline-none focus:border-primary text-sm mt-2"
            />
            <ScrollArea className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 my-3">
              <div className="border rounded-lg overflow-hidden">
                <button
                  className="w-full text-left px-4 py-2.5 hover:bg-muted active:scale-[0.98] transition text-sm border-b"
                  onClick={() => {
                    setSelectedFolderId(null);
                    setOpenFolderSheet(false);
                    setFolderSearchQuery("");
                  }}
                >
                  <div className="inline-flex items-center gap-2">
                    <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <FolderIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    No folder
                  </div>
                </button>

                {filteredFolders.length === 0 ? (
                  <div className="px-4 py-3 text-center text-xs text-muted-foreground border-t">
                    No folders found
                  </div>
                ) : (
                  filteredFolders.map((folder) => (
                    <button
                      key={folder.id}
                      className={cn(
                        "w-full text-left flex items-center gap-2 px-4 py-2.5 border-b last:border-b-0 hover:bg-muted transition",
                        selectedFolderId === folder.id && "bg-primary/5",
                      )}
                      onClick={() => {
                        setSelectedFolderId(folder.id);
                        setOpenFolderSheet(false);
                        setFolderSearchQuery("");
                      }}
                    >
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <FolderIcon className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-sm font-medium truncate">
                        {folder.name}
                      </span>
                      {selectedFolderId === folder.id && (
                        <span className="ml-auto text-xs text-primary font-medium">
                          Selected
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>

      {/* Link paste-as picker */}
      <Sheet open={linkSheetOpen} onOpenChange={setLinkSheetOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl flex flex-col max-h-[60vh] overflow-hidden [&>button.absolute]:hidden"
        >
          <div className="relative flex flex-col min-h-0">
            <div className="relative flex items-center justify-center py-1">
              <h2 className="text-sm font-semibold">Add link</h2>
              <button
                onClick={() => setLinkSheetOpen(false)}
                className="absolute right-0 h-8 w-8 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <input
              type="url"
              autoFocus
              placeholder="Paste a link"
              value={linkSheetUrl}
              onChange={(e) => setLinkSheetUrl(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-muted border border-transparent focus:outline-none focus:border-primary text-sm mt-3"
            />
            <div className="flex flex-col gap-2 mt-4 mb-2">
              {isEmbeddableVideo(linkSheetUrl) && (
                <button
                  type="button"
                  disabled={!linkSheetUrl.trim()}
                  onClick={() => insertLink("embed")}
                  className="w-full text-left px-4 py-3 rounded-xl bg-muted/60 hover:bg-muted transition text-sm font-medium disabled:opacity-40"
                >
                  Embed video
                </button>
              )}
              <button
                type="button"
                disabled={!linkSheetUrl.trim()}
                onClick={() => insertLink("bookmark")}
                className="w-full text-left px-4 py-3 rounded-xl bg-muted/60 hover:bg-muted transition text-sm font-medium disabled:opacity-40"
              >
                Bookmark
              </button>
              <button
                type="button"
                disabled={!linkSheetUrl.trim()}
                onClick={() => insertLink("url")}
                className="w-full text-left px-4 py-3 rounded-xl bg-muted/60 hover:bg-muted transition text-sm font-medium disabled:opacity-40"
              >
                URL
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
